/**
 * ユーザー詳細画面 (v3.2.7 / T7)。
 *
 * URL: /admin/users/:uid
 *
 * セクション:
 *   - 基本情報 (氏名 / メール / 部署 / レベル / owner) — 表示のみ (編集は一覧モーダル)
 *   - 請求先住所 (複数登録可、★既定 1 件、本人 + owner 編集可)
 *   - 納品先住所 (同)
 *   - 振込口座 (複数登録可、★既定 1 件、本人 + owner のみ表示・編集)
 *   - 販売記録 (admins/{uid}/sales_records、T6 で書き込み、本画面は read のみ)
 */
import type {
  Admin,
  BankAccount,
  BankAccountType,
  BillingAddress,
  DeliveryAddress,
} from '@goenhub/shared';
import { BANK_ACCOUNT_TYPE_LABELS, SYSTEM_DEPARTMENT_LABELS } from '@goenhub/shared';
import {
  ArrowLeft,
  Banknote,
  ClipboardList,
  MapPin,
  Plus,
  Save,
  Star,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { useMyPermissions } from '@/features/permissions';
import {
  useAdmin,
  useAdminSalesRecords,
  useUpdateAdminProfile,
} from '@/hooks/useAdmins';
import { useAuth } from '@/hooks/useAuth';
import { paths } from '@/routes/paths';

function genId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function UserDetailPage() {
  const { uid = '' } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { isOwner } = useMyPermissions();
  const admin = useAdmin(uid);
  const update = useUpdateAdminProfile();
  const salesRecords = useAdminSalesRecords(uid);

  const isSelf = currentUser?.uid === uid;
  const canEdit = isSelf || isOwner;

  const [billings, setBillings] = useState<BillingAddress[]>([]);
  const [deliveries, setDeliveries] = useState<DeliveryAddress[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [invoiceRegNumber, setInvoiceRegNumber] = useState<string>('');
  // 掲示板通知 (Admin.boardNotify)
  const [boardEmail, setBoardEmail] = useState(true);
  const [boardChatUrl, setBoardChatUrl] = useState('');
  const [boardChatworkRoom, setBoardChatworkRoom] = useState('');

  useEffect(() => {
    if (admin.data) {
      setBillings(admin.data.billingAddresses ?? []);
      setDeliveries(admin.data.deliveryAddresses ?? []);
      setAccounts(admin.data.bankAccounts ?? []);
      setInvoiceRegNumber(admin.data.invoiceRegistrationNumber ?? '');
      setBoardEmail(admin.data.boardNotify?.email ?? true);
      setBoardChatUrl(admin.data.boardNotify?.googleChatWebhookUrl ?? '');
      setBoardChatworkRoom(admin.data.boardNotify?.chatworkRoomId ?? '');
    }
  }, [admin.data]);

  if (admin.isLoading) {
    return (
      <Screen title="読み込み中…">
        <Spinner />
      </Screen>
    );
  }
  if (!admin.data) {
    return (
      <Screen title="ユーザーが見つかりません">
        <Button onClick={() => navigate(paths.adminUsers)}>
          <ArrowLeft className="size-3.5" /> 一覧に戻る
        </Button>
      </Screen>
    );
  }

  const a = admin.data;
  const initialBoardNotify = {
    email: admin.data.boardNotify?.email ?? true,
    googleChatWebhookUrl: admin.data.boardNotify?.googleChatWebhookUrl ?? null,
    chatworkRoomId: admin.data.boardNotify?.chatworkRoomId ?? null,
  };
  const currentBoardNotify: NonNullable<Admin['boardNotify']> = {
    email: boardEmail,
    googleChatWebhookUrl: boardChatUrl.trim() || null,
    chatworkRoomId: boardChatworkRoom.trim() || null,
  };
  const initial = {
    billings: admin.data.billingAddresses ?? [],
    deliveries: admin.data.deliveryAddresses ?? [],
    accounts: admin.data.bankAccounts ?? [],
    invoiceRegNumber: admin.data.invoiceRegistrationNumber ?? '',
    boardNotify: initialBoardNotify,
  };
  const isDirty =
    JSON.stringify({
      billings,
      deliveries,
      accounts,
      invoiceRegNumber,
      boardNotify: currentBoardNotify,
    }) !== JSON.stringify(initial);

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        adminId: uid,
        billingAddresses: billings,
        deliveryAddresses: deliveries,
        bankAccounts: accounts,
        invoiceRegistrationNumber: invoiceRegNumber.trim() || null,
        boardNotify: currentBoardNotify,
      });
      toast.success('プロフィールを保存しました');
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  return (
    <Screen
      title={a.fullName || a.displayName || a.email}
      description={a.email}
      actions={
        <div className="flex items-center gap-2">
          {isDirty && canEdit && (
            <span className="text-xs text-[var(--accent-amber)]">
              未保存の変更があります
            </span>
          )}
          {canEdit && (
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!isDirty || update.isPending}
            >
              <Save className="size-3.5" />
              {update.isPending ? '保存中…' : isDirty ? '保存' : '保存済'}
            </Button>
          )}
          <Button variant="ghost" onClick={() => navigate(paths.adminUsers)}>
            <ArrowLeft className="size-3.5" /> 一覧に戻る
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="space-y-5">
          {/* 基本情報 */}
          <Section title="基本情報">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="氏名" value={a.fullName ?? a.displayName ?? '—'} />
              <Field label="メール" value={a.email} />
              <Field
                label="部署"
                value={
                  a.dept
                    ? SYSTEM_DEPARTMENT_LABELS[
                        a.dept as keyof typeof SYSTEM_DEPARTMENT_LABELS
                      ] ?? a.dept
                    : '—'
                }
              />
              <Field label="レベル" value={a.level ?? '—'} />
            </div>
            <div className="flex items-center gap-2">
              {a.isOwner && <Badge variant="accent">★ Owner</Badge>}
              <Badge variant={a.resolvedStatus === 'active' ? 'review' : 'default'}>
                {a.resolvedStatus === 'active' ? 'アクティブ' : '手続中'}
              </Badge>
            </div>
            {!canEdit && (
              <p className="text-[11px] text-[var(--text-muted)] italic">
                住所・口座の編集は本人または owner のみ可能です。
              </p>
            )}
          </Section>

          {/* 請求先住所 */}
          <AddressListSection
            title="請求先住所"
            description="報酬支払い・請求書宛先として使用。複数登録可、★既定 1 件。"
            entries={billings}
            placeholder="請求先"
            disabled={!canEdit}
            onChange={setBillings}
          />

          {/* 納品先住所 */}
          <AddressListSection
            title="納品先住所"
            description="物品送付の宛先として使用 (将来用)。複数登録可、★既定 1 件。"
            entries={deliveries}
            placeholder="納品先"
            disabled={!canEdit}
            onChange={setDeliveries}
          />

          {/* 振込口座 */}
          {canEdit ? (
            <BankAccountsSection
              accounts={accounts}
              disabled={!canEdit}
              onChange={setAccounts}
            />
          ) : (
            <Section title="振込口座 (非公開)">
              <p className="text-xs text-[var(--text-muted)] italic">
                振込口座は本人と owner のみ閲覧できます。
              </p>
            </Section>
          )}

          {/* 適格請求書発行事業者登録番号 (インボイス制度対応) */}
          <Section
            title="適格請求書発行事業者登録番号"
            description="インボイス制度対応 (T + 13桁)。SA 請求書の消費税扱いに影響します。空欄 = 免税事業者 (税抜のみ表示)"
          >
            <div className="space-y-1">
              <input
                type="text"
                value={invoiceRegNumber}
                onChange={(e) => setInvoiceRegNumber(e.target.value)}
                disabled={!canEdit}
                placeholder="T1234567890123"
                maxLength={14}
                className="w-full px-3 py-2 text-sm rounded-sm border border-[var(--border)] bg-[var(--bg-input)] tabular-nums disabled:opacity-60"
              />
              {invoiceRegNumber && !/^T\d{13}$/.test(invoiceRegNumber.trim()) && (
                <p className="text-[10px] text-[var(--accent-rose,_#f87171)]">
                  形式: T で始まる 13 桁の数字 (例: T1234567890123)
                </p>
              )}
            </div>
          </Section>

          {/* 掲示板通知 (Phase 5) */}
          <Section
            title="掲示板通知"
            description="掲示板の宛先になった投稿・更新の通知を受け取るチャネル。メールに加え、個人宛の Google Chat / Chatwork へも送れます。"
          >
            <div className="space-y-3">
              <Switch
                label="メール通知"
                description="宛先になった投稿・更新をメールで受け取る"
                checked={boardEmail}
                disabled={!canEdit}
                onChange={(e) => setBoardEmail(e.target.checked)}
              />
              <FieldInput
                label="Google Chat Webhook URL"
                value={boardChatUrl}
                disabled={!canEdit}
                onChange={setBoardChatUrl}
                placeholder="https://chat.googleapis.com/v1/spaces/..."
              />
              <FieldInput
                label="Chatwork ルーム ID"
                value={boardChatworkRoom}
                disabled={!canEdit}
                onChange={setBoardChatworkRoom}
                placeholder="例: 123456789"
              />
            </div>
          </Section>
        </div>

        {/* 右カラム: 販売記録 */}
        <div>
          <Section
            title="販売記録"
            description="入金確定時に T6 が自動追記します (本 phase は表示のみ)"
          >
            {salesRecords.isLoading ? (
              <Spinner />
            ) : !salesRecords.data || salesRecords.data.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-6 text-[var(--text-muted)]">
                <ClipboardList className="size-8 opacity-50" />
                <p className="text-xs italic">販売記録はまだありません</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                {salesRecords.data.map((r: Record<string, unknown>) => (
                  <div
                    key={String(r.id)}
                    className="px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] text-xs"
                  >
                    <pre className="text-[10px] whitespace-pre-wrap break-all">
                      {JSON.stringify(r, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </Screen>
  );
}

// ─────────────────────────────────────────────
// 住所セクション (請求先 / 納品先 共通)
// ─────────────────────────────────────────────

function AddressListSection({
  title,
  description,
  entries,
  placeholder,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  entries: BillingAddress[] | DeliveryAddress[];
  placeholder: string;
  disabled: boolean;
  onChange: (next: BillingAddress[] & DeliveryAddress[]) => void;
}) {
  const update = (id: string, patch: Partial<BillingAddress & DeliveryAddress>) => {
    onChange(
      entries.map((a) =>
        a.id === id ? ({ ...a, ...patch } as BillingAddress & DeliveryAddress) : a,
      ) as BillingAddress[] & DeliveryAddress[],
    );
  };
  const addEntry = () => {
    const next = [
      ...entries,
      {
        id: genId(),
        label: '',
        recipientName: '',
        postalCode: '',
        prefecture: '',
        address: '',
        buildingName: '',
        phone: '',
        isDefault: entries.length === 0,
      },
    ] as BillingAddress[] & DeliveryAddress[];
    onChange(next);
  };
  const removeEntry = (id: string) => {
    const next = entries.filter((a) => a.id !== id);
    if (next.length > 0 && !next.some((a) => a.isDefault)) {
      next[0] = { ...next[0], isDefault: true };
    }
    onChange(next as BillingAddress[] & DeliveryAddress[]);
  };
  const setDefault = (id: string) => {
    onChange(
      entries.map((a) => ({ ...a, isDefault: a.id === id })) as BillingAddress[] &
        DeliveryAddress[],
    );
  };

  return (
    <Section title={title} description={description}>
      {entries.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)] italic">
          {title}が登録されていません。
        </p>
      ) : (
        <div className="space-y-3">
          {entries.map((a, idx) => (
            <div
              key={a.id}
              className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <MapPin className="size-3.5 text-[var(--text-muted)] shrink-0" />
                  <Input
                    value={a.label ?? ''}
                    onChange={(e) => update(a.id, { label: e.target.value })}
                    disabled={disabled}
                    placeholder={`${placeholder} ${idx + 1}（例: 本社、自宅）`}
                    className="text-xs"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setDefault(a.id)}
                  disabled={disabled}
                  className={`px-2 py-1 text-[10px] rounded inline-flex items-center gap-1 transition-colors ${
                    a.isDefault
                      ? 'bg-[var(--accent-amber-subtle)] text-[var(--accent-amber)]'
                      : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                  } disabled:opacity-50`}
                >
                  <Star className={`size-3 ${a.isDefault ? 'fill-current' : ''}`} />
                  {a.isDefault ? '既定' : '既定にする'}
                </button>
                <button
                  type="button"
                  onClick={() => removeEntry(a.id)}
                  disabled={disabled}
                  className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--accent-red)] hover:bg-[var(--bg-hover)] disabled:opacity-50"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FieldInput label="宛名" value={a.recipientName ?? ''} disabled={disabled} onChange={(v) => update(a.id, { recipientName: v })} />
                <FieldInput label="電話番号" value={a.phone ?? ''} disabled={disabled} onChange={(v) => update(a.id, { phone: v })} />
              </div>
              <div className="grid grid-cols-[1fr_2fr] gap-3">
                <FieldInput label="郵便番号" value={a.postalCode ?? ''} disabled={disabled} onChange={(v) => update(a.id, { postalCode: v })} placeholder="000-0000" />
                <FieldInput label="都道府県" value={a.prefecture ?? ''} disabled={disabled} onChange={(v) => update(a.id, { prefecture: v })} />
              </div>
              <FieldInput label="市区町村・番地" value={a.address ?? ''} disabled={disabled} onChange={(v) => update(a.id, { address: v })} />
              <FieldInput label="ビル名・階数" value={a.buildingName ?? ''} disabled={disabled} onChange={(v) => update(a.id, { buildingName: v })} />
            </div>
          ))}
        </div>
      )}
      {!disabled && (
        <Button variant="secondary" size="sm" onClick={addEntry}>
          <Plus className="size-3.5" />
          {placeholder}を追加
        </Button>
      )}
    </Section>
  );
}

// ─────────────────────────────────────────────
// 振込口座セクション
// ─────────────────────────────────────────────

function BankAccountsSection({
  accounts,
  disabled,
  onChange,
}: {
  accounts: BankAccount[];
  disabled: boolean;
  onChange: (next: BankAccount[]) => void;
}) {
  const update = (id: string, patch: Partial<BankAccount>) => {
    onChange(accounts.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };
  const addAccount = () => {
    onChange([
      ...accounts,
      {
        id: genId(),
        label: '',
        bankName: '',
        branchName: '',
        accountType: 'ordinary',
        accountNumber: '',
        accountHolder: '',
        isDefault: accounts.length === 0,
      },
    ]);
  };
  const remove = (id: string) => {
    const next = accounts.filter((a) => a.id !== id);
    if (next.length > 0 && !next.some((a) => a.isDefault)) {
      next[0] = { ...next[0], isDefault: true };
    }
    onChange(next);
  };
  const setDefault = (id: string) => {
    onChange(accounts.map((a) => ({ ...a, isDefault: a.id === id })));
  };

  return (
    <Section
      title="振込口座"
      description="報酬支払い時に「★既定」を含む 1 件を選択します。本人と owner のみ閲覧可。"
    >
      {accounts.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)] italic">
          振込口座が登録されていません。
        </p>
      ) : (
        <div className="space-y-3">
          {accounts.map((a, idx) => (
            <div
              key={a.id}
              className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Banknote className="size-3.5 text-[var(--text-muted)] shrink-0" />
                  <Input
                    value={a.label ?? ''}
                    onChange={(e) => update(a.id, { label: e.target.value })}
                    disabled={disabled}
                    placeholder={`口座 ${idx + 1}（例: メイン）`}
                    className="text-xs"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setDefault(a.id)}
                  disabled={disabled}
                  className={`px-2 py-1 text-[10px] rounded inline-flex items-center gap-1 transition-colors ${
                    a.isDefault
                      ? 'bg-[var(--accent-amber-subtle)] text-[var(--accent-amber)]'
                      : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                  } disabled:opacity-50`}
                >
                  <Star className={`size-3 ${a.isDefault ? 'fill-current' : ''}`} />
                  {a.isDefault ? '既定' : '既定にする'}
                </button>
                <button
                  type="button"
                  onClick={() => remove(a.id)}
                  disabled={disabled}
                  className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--accent-red)] hover:bg-[var(--bg-hover)] disabled:opacity-50"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FieldInput label="銀行名" value={a.bankName} disabled={disabled} onChange={(v) => update(a.id, { bankName: v })} placeholder="例: 三菱UFJ銀行" />
                <FieldInput label="支店名" value={a.branchName} disabled={disabled} onChange={(v) => update(a.id, { branchName: v })} placeholder="例: 渋谷支店" />
              </div>
              <div className="grid grid-cols-[1fr_2fr] gap-3">
                <div>
                  <Label className="text-xs">種別</Label>
                  <select
                    value={a.accountType}
                    onChange={(e) =>
                      update(a.id, { accountType: e.target.value as BankAccountType })
                    }
                    disabled={disabled}
                    className="mt-1 w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)] disabled:opacity-50"
                  >
                    {(['ordinary', 'checking', 'savings'] as const).map((t) => (
                      <option key={t} value={t}>
                        {BANK_ACCOUNT_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </div>
                <FieldInput label="口座番号" value={a.accountNumber} disabled={disabled} onChange={(v) => update(a.id, { accountNumber: v })} placeholder="7桁" />
              </div>
              <FieldInput
                label="口座名義 (カナ)"
                value={a.accountHolder}
                disabled={disabled}
                onChange={(v) => update(a.id, { accountHolder: v })}
                placeholder="例: テラオカ ユウキ"
              />
            </div>
          ))}
        </div>
      )}
      {!disabled && (
        <Button variant="secondary" size="sm" onClick={addAccount}>
          <Plus className="size-3.5" />
          口座を追加
        </Button>
      )}
    </Section>
  );
}

// ─────────────────────────────────────────────
// 共通
// ─────────────────────────────────────────────

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">{title}</h3>
        {description && (
          <p className="text-xs text-[var(--text-muted)] mt-0.5">{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
        {label}
      </div>
      <div className="text-sm text-[var(--text-secondary)] truncate">{value}</div>
    </div>
  );
}

function FieldInput({
  label,
  value,
  onChange,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs text-[var(--text-muted)] mb-1">{label}</span>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
      />
    </label>
  );
}
